import { registerClass } from '../SatCoreRegistry';

import guidedTourManager from '../managers/GuidedTourManager';
import userManager from '../managers/UserManager';

import { getLocalStorageItem, setLocalStorageItem } from '../utils';

/**
 * Service for initializing and launching guided tours.
 *
 * This service is **configuration-driven** via `guidedTourManager` observables with
 * **role-specific configurations**. Each role (districtAdmin, schoolAdmin, teacher,
 * student, generic) can have its own guided tour provider and settings.
 *
 * Configuration can be set in satellite `index.js` or via `satelliteLayout.json`
 * (for dynamic satellites).
 *
 * **Currently Supported Providers:**
 * - Navattic
 *
 * **To Add Future Providers:**
 * See the skeleton implementations at the bottom of this file for examples of how to add
 * new providers like Pendo, WalkMe, Appcues, etc.
 *
 * @example
 * // In satellite index.js (for static/non-dynamic satellites)
 * guidedTourManager.setGuidedTourConfig({
 *   districtAdmin: {
 *     autoShowTourOnFirstVisit: true,
 *     guidedTourId: 'visited-guided-tour-district-admin',
 *     guidedTourProvider: 'navattic',
 *     guidedTourUrl: 'https://capture.navattic.com/abc123',
 *     navatticConfig: { title: 'District Admin Tour' }
 *   },
 *   schoolAdmin: { ... },
 *   teacher: { ... },
 *   student: { ... },
 *   generic: { ... }
 * });
 *
 * @example
 * // In satelliteLayout.json (for dynamic satellites)
 * {
 *   "satelliteIndexFlagOverrides": {
 *     "guidedTourConfig": {
 *       "districtAdmin": {
 *         "autoShowTourOnFirstVisit": true,
 *         "guidedTourId": "visited-guided-tour-district-admin",
 *         "guidedTourProvider": "navattic",
 *         "guidedTourUrl": "https://capture.navattic.com/abc123",
 *         "navatticConfig": { "title": "District Admin Tour" }
 *       },
 *       "schoolAdmin": { ... },
 *       "teacher": { ... },
 *       "student": { ... },
 *       "generic": { ... }
 *     }
 *   }
 * }
 *
 * @example
 * // Initialize on app load (called by GuidedTourLauncher component)
 * GuidedTourService.initGuidedTour();
 *
 * // Launch from profile menu
 * GuidedTourService.launchGuidedTour();
 */
export default class GuidedTourService {
  /**
   * Check if guided tour launcher should be allowed for the current user.
   *
   * @returns {boolean} True if guided tour is configured for the current user role
   */
  static shouldAllowGuidedTourLauncher = () => {
    const roleConfig = this.getRoleSpecificConfig();
    return !!(roleConfig?.guidedTourProvider);
  }

  /**
   * Get the role-specific guided tour configuration for the current user.
   *
   * @returns {Object} Role-specific configuration
   */
  static getRoleSpecificConfig = () => {
    const guidedTourConfig = guidedTourManager.guidedTourConfig || {};

    // Check for specific roles (user can only have one active role at a time)
    const {
      isDistrictAdmin,
      isDistrictAssessmentAdmin,
      isDistrictProductAdmin,
      isSchoolAdmin,
      isSchoolAssessmentAdmin,
      isSchoolProductAdmin,
      isTeacher,
      isStudent
    } = userManager;

    // District admin roles
    if (isDistrictAdmin && guidedTourConfig.districtAdmin) {
      return guidedTourConfig.districtAdmin;
    }
    if (isDistrictAssessmentAdmin && guidedTourConfig.districtAssessmentAdmin) {
      return guidedTourConfig.districtAssessmentAdmin;
    }
    if (isDistrictProductAdmin && guidedTourConfig.districtProductAdmin) {
      return guidedTourConfig.districtProductAdmin;
    }

    // School admin roles
    if (isSchoolAdmin && guidedTourConfig.schoolAdmin) {
      return guidedTourConfig.schoolAdmin;
    }
    if (isSchoolAssessmentAdmin && guidedTourConfig.schoolAssessmentAdmin) {
      return guidedTourConfig.schoolAssessmentAdmin;
    }
    if (isSchoolProductAdmin && guidedTourConfig.schoolProductAdmin) {
      return guidedTourConfig.schoolProductAdmin;
    }

    // Teacher role
    if (isTeacher && guidedTourConfig.teacher) {
      return guidedTourConfig.teacher;
    }

    // Student role
    if (isStudent && guidedTourConfig.student) {
      return guidedTourConfig.student;
    }

    // Fallback to generic config
    return guidedTourConfig.generic || {};
  }

  /**
   * Initialize the configured guided tour provider.
   *
   * This should be called during app initialization (e.g., after login).
   * It loads the necessary third-party scripts and prepares the tour for launching.
   */
  static initGuidedTour = () => {
    const roleConfig = this.getRoleSpecificConfig();
    const { guidedTourProvider } = roleConfig || {};

    switch (guidedTourProvider) {
      case 'navattic':
        this.initNavattic();
        break;
      // Add future providers here - see skeleton implementations below
      default:
        // No guided tour provider configured
        break;
    }
  }

  /**
   * Launch the configured guided tour.
   *
   * This is typically called from the profile menu "Guided Tour" option.
   */
  static launchGuidedTour = () => {
    const roleConfig = this.getRoleSpecificConfig();
    const { guidedTourProvider } = roleConfig || {};

    switch (guidedTourProvider) {
      case 'navattic':
        this.launchNavattic();
        break;
      // Add future providers here - see skeleton implementations below
      default:
        console.warn('No guided tour provider configured');
        break;
    }
  }

  /**
   * Check if the guided tour should auto-show and has not been shown before.
   *
   * Uses localStorage to track if the tour has been shown.
   *
   * @returns {boolean} True if tour should auto-show
   */
  static shouldAutoShowGuidedTour = () => {
    const roleConfig = this.getRoleSpecificConfig();
    const { autoShowTourOnFirstVisit, guidedTourId, guidedTourProvider } = roleConfig || {};

    if (!autoShowTourOnFirstVisit || !guidedTourProvider) {
      return false;
    }

    // Check localStorage to see if tour has been shown
    const storageKey = guidedTourId || 'visited-guided-tour-generic';
    const hasBeenShown = getLocalStorageItem(storageKey);

    return !hasBeenShown;
  }

  /**
   * Mark the guided tour as shown in localStorage.
   *
   * This prevents the tour from auto-showing again on subsequent visits.
   */
  static markGuidedTourAsShown = () => {
    const roleConfig = this.getRoleSpecificConfig();
    const { guidedTourId, guidedTourProvider } = roleConfig || {};

    if (!guidedTourProvider) {
      return;
    }

    const storageKey = guidedTourId || 'visited-guided-tour-generic';
    setLocalStorageItem(storageKey, 'true');
  }

  /**
   * Remove guided tour launcher scripts and clean up if applicable.
   *
   * This is typically called on logout to clean up third-party scripts.
   * Checks all possible providers and removes them if present.
   */
  static removeGuidedTourLauncherIfApplicable = () => {
    // Remove Navattic if present
    if (window.NavatticEmbed) {
      try {
        this.removeNavattic();
      } catch (error) {
        console.warn('Error removing Navattic:', error);
      }
    }
    // Add future provider cleanup here (Pendo, WalkMe, etc.)
  }

  // ============================================================================
  // NAVATTIC IMPLEMENTATION
  // ============================================================================

  /**
   * Initialize Navattic.
   *
   * Loads the Navattic embed script and initializes the popup with the role-specific configuration.
   *
   * **Required Configuration:**
   * ```js
   * {
   *   autoShowTourOnFirstVisit: true, // Optional, auto-launch tour on first visit
   *   guidedTourId: 'unique-tour-id', // Optional, used for localStorage tracking
   *   guidedTourProvider: 'navattic',
   *   guidedTourUrl: 'https://capture.navattic.com/...',
   *   loaderScriptId: 'custom-script-id', // Optional, defaults to 'navattic-script-loader'
   *   loaderScriptSrc: 'https://js.navattic.com/embeds.js', // Optional, defaults to Navattic embeds.js
   *   navatticConfig: { title: 'Tour Title' } // Optional Navattic-specific config
   * }
   * ```
   *
   * @see https://docs.navattic.com for Navattic documentation
   */
  static initNavattic = () => {
    const roleConfig = this.getRoleSpecificConfig();
    const {
      autoShowTourOnFirstVisit,
      guidedTourUrl,
      loaderScriptId,
      loaderScriptSrc,
      navatticConfig
    } = roleConfig || {};

    if (!guidedTourUrl) {
      // No guided tour URL configured for this role - silently skip initialization
      return;
    }

    // Check if script already exists to prevent duplicate loading
    const scriptId = loaderScriptId || 'navattic-script-loader';
    const existingScript = document.getElementById(scriptId);

    if (!existingScript) {
      // Load Navattic embed script
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = loaderScriptSrc || 'https://js.navattic.com/embeds.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // Wait for Navattic to be ready, then initialize with retry logic
    const ALLOWED_TIMES_TRIED = 10;
    let timesTried = 0;

    const interval = setInterval(() => {
      if (window.NavatticEmbed) {
        try {
          window.NavatticEmbed.initPopup(guidedTourUrl, navatticConfig || {});
          clearInterval(interval);

          // Auto-show tour if configured and not shown before
          if (autoShowTourOnFirstVisit && this.shouldAutoShowGuidedTour()) {
            // Small delay to ensure popup is fully initialized
            setTimeout(() => {
              this.launchNavattic();
              this.markGuidedTourAsShown();
            }, 500);
          }
        } catch (error) {
          console.warn('Error initializing Navattic:', error);
          clearInterval(interval);
        }
      } else {
        timesTried++;
        if (timesTried >= ALLOWED_TIMES_TRIED) {
          console.warn('Navattic initialization failed after', ALLOWED_TIMES_TRIED, 'attempts');
          clearInterval(interval);
        }
      }
    }, 500);
  }

  /**
   * Launch Navattic popup.
   *
   * Opens the Navattic interactive demo in a popup/modal using the role-specific configuration.
   */
  static launchNavattic = () => {
    const roleConfig = this.getRoleSpecificConfig();
    const { guidedTourUrl } = roleConfig || {};

    if (!window.NavatticEmbed) {
      console.warn('NavatticEmbed not initialized. Call initNavattic() first.');
      return;
    }

    if (!guidedTourUrl) {
      console.warn('Guided tour URL not configured for current user role');
      return;
    }

    window.NavatticEmbed.openPopup(guidedTourUrl);
  }

  /**
   * Remove Navattic lightbox elements from the DOM.
   *
   * Removes all Navattic lightbox elements to close any open lightboxes
   * and prevent "Lightbox already exists" errors.
   *
   * Note: We do not attempt to remove the script loader or NavatticEmbed object
   * because it causes issues and there does not appear to be a straightforward documented way
   * to cleanup Navattic script content after initialization.
   */
  static removeNavattic = () => {
    // Remove all Navattic lightbox elements from DOM
    // Query for all elements with IDs containing 'navattic-lightbox'
    const lightboxElements = document.querySelectorAll('[id*="navattic-lightbox"]');
    lightboxElements.forEach((element) => {
      element.remove();
    });
  }

  // ============================================================================
  // SKELETON IMPLEMENTATIONS FOR FUTURE PROVIDERS
  // ============================================================================
  //
  // To add a new provider:
  // 1. Uncomment and implement the init and launch methods below
  // 2. Add the provider case to initGuidedTour() and launchGuidedTour()
  // 3. Update GuidedTourManager JSDoc with the new configuration structure
  // 4. Test the implementation in a satellite
  //
  // ============================================================================

  /**
   * SKELETON: Initialize Pendo (not yet implemented).
   *
   * @see https://developers.pendo.io for Pendo documentation
   */
  // static initPendo = () => {
  //   const { pendoApiKey } = guidedTourManager.guidedTourConfig;
  //   if (!pendoApiKey) {
  //     console.warn('Pendo API key not configured');
  //     return;
  //   }
  //   // Load Pendo script with API key
  //   // Initialize Pendo with visitor/account data
  // }

  /**
   * SKELETON: Launch Pendo guide (not yet implemented).
   */
  // static launchPendo = () => {
  //   if (!window.pendo) {
  //     console.warn('Pendo not initialized');
  //     return;
  //   }
  //   const { pendoGuideId } = guidedTourManager.guidedTourConfig;
  //   // Launch specific guide: window.pendo.showGuideById(pendoGuideId);
  // }

  /**
   * SKELETON: Initialize WalkMe (not yet implemented).
   *
   * @see https://developer.walkme.com/reference/overview for WalkMe documentation
   */
  // static initWalkMe = () => {
  //   const { walkMeId } = guidedTourManager.guidedTourConfig;
  //   if (!walkMeId) {
  //     console.warn('WalkMe ID not configured');
  //     return;
  //   }
  //   // Load WalkMe script
  // }

  /**
   * SKELETON: Launch WalkMe walk-thru (not yet implemented).
   */
  // static launchWalkMe = () => {
  //   if (!window.WalkMe) {
  //     console.warn('WalkMe not initialized');
  //     return;
  //   }
  //   // Launch WalkMe walk-thru
  // }

  /**
   * SKELETON: Initialize Appcues (not yet implemented).
   *
   * @see https://docs.appcues.com for Appcues documentation
   */
  // static initAppcues = () => {
  //   const { appcuesAccountId } = guidedTourManager.guidedTourConfig;
  //   if (!appcuesAccountId) {
  //     console.warn('Appcues account ID not configured');
  //     return;
  //   }
  //   // Load Appcues script
  // }

  /**
   * SKELETON: Launch Appcues flow (not yet implemented).
   */
  // static launchAppcues = () => {
  //   if (!window.Appcues) {
  //     console.warn('Appcues not initialized');
  //     return;
  //   }
  //   // Launch Appcues flow
  // }
}

registerClass('GuidedTourService', GuidedTourService);
